import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { Modal } from '@mui/material';
import { FiTrash2 } from 'react-icons/fi';
import { formatDate, formatPhone, getUserInfoDetails, mapToOption, formatDateCST, formatTimeCST } from '../../../../utlis';
import { createMarketingJob, updateMarketingJob, fetchJobList, fetchMarketingList } from '../../../../api/candidiate';
import { updateSalesContact } from '../../../../api/sales';
import axiosInstance from '../../../../api/axiosInstance';
import useFetch from '../../../../hooks/useFetch';
import MarketingJobsHeader from './components/MarketingJobsHeader';
import MarketingJobsTable from './components/MarketingJobsTable';
import SalesContacts from '../../../Sales/Contacts/SalesContacts';
import SalesContactsModal from '../../../Sales/Contacts/components/SalesContactsModal';
import AssignJobFromCandidateModal from './components/AssignJobFromCandidateModal';

const MarketingJobs = () => {
  const { companyId, clientId } = useMemo(() => getUserInfoDetails(), []);
  const candidateId = useSelector((state) => state.personal.candidateId);
  const referenceData = useSelector((state) => state.reference);

  const initialMarketingJob = {
    jobTitle: '',
    vendorId: '',
    durationMonths: '',
    client: '',
    technology: '',
    numOfPositions: '',
    sales_person_id: '',
    billRate: '',
    payType: '',
    state: '',
    city: '',
    createdOn: '',
    status: '',
    statusReason: '',
    jobContactName: '',
    jobContactEmail: '',
    jobContactPhoneNum: '',
    jobDescription: '',
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({ mode: 'onChange' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [marketingJob, setMarketingJob] = useState(initialMarketingJob);
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Contact selection state
  const [isContactsOpen, setIsContactsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Edit contact modal state
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editContactData, setEditContactData] = useState(null);

  // Assign Job modal state
  const [isAssignJobModalOpen, setIsAssignJobModalOpen] = useState(false);
  const [hasActiveMarketing, setHasActiveMarketing] = useState(false);
  const [candidateSalesPersonId, setCandidateSalesPersonId] = useState(null);

  // Reference data
  const { data: refSalesPerson, loading: refSalesLoading } = useFetch('/salesteam', {
    company: companyId,
    client: clientId,
  });
  const { data: refVendor, loading: refVendorLoading } = useFetch('/vendors', {
    company: companyId,
    client: clientId,
  });
  const { data: refTechnologies, loading: refTechnologiesLoading } = useFetch('/technology', {
    company: companyId,
    client: clientId,
  });
  const { data: refContacts, refetch: refetchContacts } = useFetch('/contacts', {
    company: companyId,
    client: clientId,
  });
  const contactsData = refContacts?.data?.records || [];

  // Contact selection handler
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setIsContactsModalOpen(false);
  };

  useEffect(() => {
    if (selectedContact) {
      setValue('jobContactName', `${selectedContact.firstName} ${selectedContact.lastName}`, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('jobContactEmail', selectedContact.email, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('jobContactPhoneNum', selectedContact.mobileNum1, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('vendor_contact_id', selectedContact.contact_id, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [selectedContact, setValue]);

  // Data fetching
  const getData = async () => {
    setLoading(true);
    try {
      const res = await fetchJobList({ candidateId });
      setData(res.data.data.records || []);
    } catch (error) {
      toast.error('Failed to fetch job data');
    } finally {
      setLoading(false);
    }
  };


  // Check if candidate has an active marketing_history record
  const checkActiveMarketing = async () => {
    if (!candidateId) return;
    try {
      const res = await fetchMarketingList({ candidateId, pageNum: 1, pageSize: 100 });
      const records = res.data?.response?.data || [];
      const activeRecord = records.find((r) => r.status === 'Active');
      setHasActiveMarketing(!!activeRecord);
      if (activeRecord) {
        setCandidateSalesPersonId(activeRecord.sales_person_id || null);
      }
    } catch (error) {
      setHasActiveMarketing(false);
    }
  };

  useEffect(() => {
    if (candidateId) {
      getData();
      checkActiveMarketing();
    }
  }, [candidateId]);

  // Assign Job modal handlers
  const openAssignJobModal = () => {
    if (!hasActiveMarketing) return;
    setIsAssignJobModalOpen(true);
  };

  const closeAssignJobModal = () => {
    setIsAssignJobModalOpen(false);
  };

  // Delete job handler - updates status to 'Deleted'
  const handleDeleteJob = async (jobId) => {
    if (!jobId) return;
    try {
      await axiosInstance.put(`/jobs/${jobId}`, { status: 'Deleted' });
      toast.success('Job deleted successfully');
      getData();
    } catch (error) {
      const errMsg = error?.response?.data?.details || error?.response?.data?.message || error.message;
      toast.error(errMsg || 'Failed to delete job');
    }
  };

  // Modal handlers
  const openModalForNewJob = () => {
    setMarketingJob(initialMarketingJob);
    reset(initialMarketingJob);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setMarketingJob(initialMarketingJob);
    reset(initialMarketingJob);
  };

  const prepareUpdate = (job_id) => {
    const jobData = data.find((d) => d.job_id === job_id);
    if (!jobData) {
      toast.error('Job not found');
      return;
    }
    const createdDate = new Date(jobData.created);

    const currentJob = {
      jobTitle: jobData.job_title,
      jobId: jobData.job_id,
      vendorId: jobData.vendor_id,
      durationMonths: jobData.duration,
      client: jobData.client,
      technology: jobData.technology_id,
      numOfPositions: jobData.no_of_openings,
      sales_person_id: jobData.sales_person_id,
      billRate: jobData.bill_rate,
      payType: jobData.pay_type,
      state: jobData.state,
      city: jobData.city,
      createdOn: createdDate.toISOString().split('T')[0] || '',
      status: jobData.status || '',
      statusReason: jobData.status_reason || '',
      jobContactName: jobData.contact_name,
      jobContactEmail: jobData.contact_email,
      jobContactPhoneNum: jobData?.contact_phone?.mobile,
      jobDescription: jobData.job_desc,
      vendor_contact_id: jobData.vendor_contact_id,
      // Audit fields
      created: jobData.created || '',
      created_by: jobData.created_by || '',
      updated: jobData.updated || '',
      updated_by: jobData.updated_by || '',
    };

    setMarketingJob(currentJob);
    reset(currentJob, { keepDefaultValues: false });
    setIsModalOpen(true);
  };

  // Open edit contact modal
  const openEditContactModal = (vendorContactId) => {
    if (!vendorContactId) {
      toast.error('No contact associated with this job');
      return;
    }

    const contact = contactsData.find((c) => c.contact_id === vendorContactId);
    if (!contact) {
      toast.error('Contact not found');
      return;
    }

    const createdDate = new Date(contact.created);
    const updatedDate = new Date(contact.updated);

    const contactData = {
      firstName: contact.first_name,
      middleName: contact.middle_name,
      lastName: contact.last_name,
      preferredName: contact.preferred_name,
      email: contact.email,
      jobTitle: contact.job_title,
      mobileNum1: contact?.phone?.mobile,
      mobileNum2: contact?.phone?.phone,
      fax: contact?.phone?.Fax || '',
      contactType: contact.contact_type || '',
      address1: contact.address?.address_line1,
      address2: contact.address?.address_line2,
      city: contact.address?.city,
      state: contact.address?.state,
      country: contact.address?.country,
      zipCode: contact.address?.zip,
      notes: contact.note || '',
      vendorStatus: contact.status || 'Active',
      contact_id: contact.contact_id,
      createdDate: createdDate.toISOString().split('T')[0] || '',
      createdTime: createdDate.toISOString().split('T')[1].slice(0, 5) || '',
      updateDate: updatedDate.toISOString().split('T')[0] || '',
      updateTime: updatedDate.toISOString().split('T')[1].slice(0, 5) || '',
      created_by: contact.created_by,
      updated_by: contact.updated_by,
    };

    setEditContactData(contactData);
    setIsEditContactModalOpen(true);
  };

  // Handle edit contact form submission
  const handleEditContactSubmit = async (formData) => {
    const payload = {
      divison: 'div',
      type: formData.contactType,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      preferredName: formData.preferredName,
      jobTitle: formData.jobTitle,
      phone: {
        mobile: formData.mobileNum1,
        phone: formData.mobileNum2,
        Fax: formData.fax,
      },
      address: {
        address_line2: formData.address2,
        address_line1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zipCode,
        country: formData.country,
      },
      status: formData.vendorStatus || 'Active',
      contact_id: formData.contact_id,
    };

    try {
      await updateSalesContact(payload);
      setIsEditContactModalOpen(false);
      setEditContactData(null);
      refetchContacts();
      getData();
      toast.success('Contact Updated Successfully');
    } catch (error) {
      toast.error('Something went wrong while updating contact');
    }
  };

  // Form submit
  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      if (marketingJob.jobId) {
        await updateMarketingJob({ ...formData, jobId: marketingJob.jobId }, candidateId);
        toast.success('Job Updated Successfully');
      } else {
        await createMarketingJob(formData, candidateId);
        toast.success('Job Created Successfully');
      }
      closeModal();
      getData();
    } catch (error) {
      toast.error('Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  // Search handler
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter handlers
  const handleFilterClick = () => setFilterPopupOpen(true);
  const handleCloseFilterPopup = () => setFilterPopupOpen(false);
  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (term, selectedCategory) => {
    if (!term || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';
        switch (selectedCategory) {
          case 'Job Title': valueToSearch = item.job_title || ''; break;
          case 'Vendor Name': valueToSearch = item.vendor_name || ''; break;
          case 'Client': valueToSearch = item.client || ''; break;
          case 'Duration': valueToSearch = item.duration?.toString() || ''; break;
          case 'Bill Rate': valueToSearch = item.bill_rate?.toString() || ''; break;
          case 'Sales Person': valueToSearch = item.sales_person_name || ''; break;
          case 'Job Location': valueToSearch = item.city || ''; break;
          case 'Technology': valueToSearch = item.technology_desc || ''; break;
          default: valueToSearch = '';
        }
        return valueToSearch.toLowerCase().includes(term.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'Job Title': return item.job_title;
          case 'Vendor Name': return item.vendor_name;
          case 'Client': return item.client;
          case 'Duration': return item.duration?.toString();
          case 'Bill Rate': return item.bill_rate?.toString();
          case 'Sales Person': return item.sales_person_name;
          case 'Job Location': return item.city;
          case 'Technology': return item.technology_desc;
          default: return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Apply search and filters to data
  useEffect(() => {
    if (!data.length) {
      setFilteredData([]);
      return;
    }

    let result = data;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) =>
        (item.job_title || '').toLowerCase().includes(searchLower) ||
        (item.vendor_name || '').toLowerCase().includes(searchLower) ||
        (item.client || '').toLowerCase().includes(searchLower) ||
        (item.technology_desc || '').toLowerCase().includes(searchLower) ||
        (item.city || '').toLowerCase().includes(searchLower) ||
        (item.sales_person_name || '').toLowerCase().includes(searchLower)
      );
    }

    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      result = result.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;
          switch (category) {
            case 'Job Title':
              return items.some((t) => t.toLowerCase() === (item.job_title || '').toLowerCase());
            case 'Vendor Name':
              return items.some((n) => n.toLowerCase() === (item.vendor_name || '').toLowerCase());
            case 'Client':
              return items.some((c) => c.toLowerCase() === (item.client || '').toLowerCase());
            case 'Duration':
              return items.some((d) => d === item.duration?.toString());
            case 'Bill Rate':
              return items.some((r) => r === item.bill_rate?.toString());
            case 'Sales Person':
              return items.some((s) => s.toLowerCase() === (item.sales_person_name || '').toLowerCase());
            case 'Job Location':
              return items.some((c) => c.toLowerCase() === (item.city || '').toLowerCase());
            case 'Technology':
              return items.some((t) => t.toLowerCase() === (item.technology_desc || '').toLowerCase());
            default:
              return false;
          }
        });
      });
    }

    setFilteredData(result);
  }, [appliedFilters, data, searchTerm]);

  const filterCategories = [
    'Job Title',
    'Vendor Name',
    'Client',
    'Duration',
    'Bill Rate',
    'Sales Person',
    'Job Location',
    'Technology',
  ];

  // Column definitions - matching JobLeads exactly
  const columns = [
    {
      name: 'job_id',
      label: 'ID',
      options: { display: false },
    },
    {
      name: 'contact_phone',
      label: 'contact_phone',
      options: { display: false },
    },
    {
      name: 'contact_name',
      label: 'contact_name',
      options: { display: false },
    },
    {
      name: 'vendor_contact_id',
      label: 'vendor_contact_id',
      options: { display: false },
    },
    {
      name: 'job_title',
      label: 'Job Title',
      options: {
        customBodyRender: (value, tableMeta) => {
          const jobId = tableMeta.rowData[0];
          const jobDesc = tableMeta.rowData[tableMeta.columnIndex + 1];
          return (
            <>
              <h3
                onClick={() => prepareUpdate(jobId)}
                className="text-base font-bold text-auxiliary-800 cursor-pointer"
              >
                {value}
              </h3>
              <p className="text-text-hint">{jobDesc}</p>
            </>
          );
        },
      },
    },
    {
      name: 'vendor_name',
      label: 'Vendor Name',
      options: { filter: true, sort: true },
    },
    {
      name: 'client',
      label: 'Client',
      options: { filter: true, sort: true },
    },
    {
      name: 'duration',
      label: 'Duration',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => `${value} months`,
      },
    },
    {
      name: 'bill_rate',
      label: 'Bill Rate',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => `${value} USD`,
      },
    },
    {
      name: 'sales_person_name',
      label: 'Sales Person Name',
      options: { filter: true, sort: true },
    },
    {
      name: 'city',
      label: 'Job Location',
    },
    {
      name: 'technology_desc',
      label: 'Technology Description',
    },
    {
      name: 'created',
      label: 'Created on',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => formatDate(value),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="py-2 rounded-full w-32 text-center text-secondary-500 activecolor">
            {value || 'Completed'}
          </p>
        ),
      },
    },
    {
      name: 'status',
      label: 'Job Contact',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const contactName = tableMeta.rowData[2];
          const vendorContactId = tableMeta.rowData[3];
          const contactPhone = tableMeta.rowData[1]?.mobile;

          const contact = contactsData.find((c) => c.contact_id === vendorContactId);
          const contactEmail = contact?.email || 'N/A';

          return (
            <div className="flex flex-col gap-1 py-1">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  openEditContactModal(vendorContactId);
                }}
                className="text-sm font-semibold text-auxiliary-800 cursor-pointer hover:underline"
              >
                {contactName || 'N/A'}
              </div>
              <div className="text-xs text-text-hint">{contactEmail}</div>
              <div className="text-xs text-text-hint">{formatPhone(contactPhone) || 'N/A'}</div>
            </div>
          );
        },
      },
    },
    {
      name: 'job_id',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        customBodyRender: (value) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteJob(value);
            }}
            className="text-danger-500 hover:text-danger-700 cursor-pointer p-1"
            title="Delete"
          >
            <FiTrash2 size={18} />
          </button>
        ),
      },
    },
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => setPageSize(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch: !isLoading && (!filteredData || filteredData.length === 0)
          ? 'Sorry, no matching records found'
          : '',
      },
    },
  };

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <MarketingJobsHeader
          onReload={getData}
          onAddJob={openModalForNewJob}
          onAssignJob={openAssignJobModal}
          assignJobEnabled={hasActiveMarketing}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <MarketingJobsTable
            data={isLoading ? [] : filteredData}
            columns={columns}
            options={options}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Job Modal */}
      <div>
        <Modal open={isModalOpen} onClose={closeModal}>
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[1100px] max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
              {/* Title and Close */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-secondary-800">
                  {marketingJob.jobId ? 'Edit Job Lead' : 'Add Job Lead'}
                </h2>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>

              <form
                className="flex flex-col gap-4 md:gap-6"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                {/* Row 1: Job Title, Vendor, Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-10">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="job_title" className="text-text-hint mb-1">
                      Job Title <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobTitle', { required: 'Job Title is required' })}
                      name="jobTitle"
                      type="text"
                      placeholder="Job Title"
                      id="job_title"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.jobTitle && (
                      <span className="text-red-500">{errors.jobTitle.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="vendor" className="text-text-hint mb-1">
                      Vendor <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('vendorId', { required: 'Vendor is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {!refVendorLoading &&
                        mapToOption(refVendor.response?.data, 'name', 'vendor_id').map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        ))}
                    </select>
                    {errors.vendorId && (
                      <span className="text-red-500">{errors.vendorId.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="durationMonths" className="text-text-hint mb-1">
                      Duration in Months <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('durationMonths', {
                        required: 'Duration Months is required',
                        pattern: { value: /^\d+$/, message: 'Must be a number' },
                      })}
                      placeholder="Duration Months"
                      id="durationMonths"
                      name="durationMonths"
                      type="text"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.durationMonths && (
                      <span className="text-red-500">{errors.durationMonths.message}</span>
                    )}
                  </div>
                </div>

                {/* Row 2: Client, Technology, Number of Positions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="client" className="text-text-hint mb-1">
                      Client <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('client', { required: 'Client is required' })}
                      name="client"
                      type="text"
                      placeholder="Client"
                      id="client"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.client && <span className="text-red-500">{errors.client.message}</span>}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="technology" className="text-text-hint mb-1">
                      Technology <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('technology', { required: 'Technology is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {!refTechnologiesLoading &&
                        mapToOption(
                          refTechnologies?.data?.records,
                          'technology_name',
                          'technology_id',
                        ).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        ))}
                    </select>
                    {errors.technology && (
                      <span className="text-red-500">{errors.technology.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="numOfPositions" className="text-text-hint mb-1">
                      Number of Positions <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('numOfPositions', {
                        required: 'Number of Positions is required',
                        pattern: { value: /^\d+$/, message: 'Must be a number' },
                      })}
                      placeholder="Number of Positions"
                      type="text"
                      id="numOfPositions"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.numOfPositions && (
                      <span className="text-red-500">{errors.numOfPositions.message}</span>
                    )}
                  </div>
                </div>

                {/* Row 3: Sales Person, Bill Rate, Pay Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="sales_person" className="text-text-hint mb-1">
                      Sales Person <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('sales_person_id', { required: 'Sales Person is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {!refSalesLoading &&
                        mapToOption(refSalesPerson?.data?.records, 'first_name', 'manager_id').map(
                          (option) => (
                            <option key={String(option.value)} value={option.value}>
                              {option.text}
                            </option>
                          ),
                        )}
                    </select>
                    {errors.sales_person_id && (
                      <span className="text-red-500">{errors.sales_person_id.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="billRate" className="text-text-hint mb-1">
                      Bill Rate <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('billRate', {
                        required: 'Bill Rate is required',
                        maxLength: {
                          value: 6,
                          message: 'Bill Rate must be 3 digits before decimal and 2 after decimal at most',
                        },
                        pattern: {
                          value: /^\d{1,3}(\.\d{1,2})?$/,
                          message: 'Must be a valid rate with up to 3 digits before decimal and up to 2 after decimal',
                        },
                      })}
                      placeholder="Bill Rate"
                      id="billRate"
                      type="text"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.billRate && (
                      <span className="text-red-500">{errors.billRate.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="pay_type" className="text-text-hint mb-1">
                      Pay Type <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('payType', { required: 'Pay Type is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {mapToOption(referenceData.paytypes?.Pay, 'type').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.payType && (
                      <span className="text-red-500">{errors.payType.message}</span>
                    )}
                  </div>
                </div>

                {/* Row 4: State, City, Select Contact */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="state" className="text-text-hint mb-1">
                      State <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('state', { required: 'State is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {mapToOption(referenceData?.states?.state, 'name').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.state && <span className="text-red-500">{errors.state.message}</span>}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="city" className="text-text-hint mb-1">
                      City <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('city', { required: 'City is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      {mapToOption(referenceData?.cities?.city, 'name').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.city && <span className="text-red-500">{errors.city.message}</span>}
                  </div>
                  <div className="flex flex-col justify-end w-72">
                    <button
                      type="button"
                      onClick={() => setIsContactsModalOpen(true)}
                      className="bg-secondary-700 text-text-light outline-none border-2 border-secondary-500 rounded-full py-2 text-center"
                    >
                      Select Contact
                    </button>
                  </div>
                </div>

                {/* Row 5: Status */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="status" className="text-text-hint mb-1">
                      Status <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('status', { required: 'Status is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      <option value="">Select Status</option>
                      {referenceData?.jobstatuses?.status && mapToOption(referenceData.jobstatuses.status, 'status').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.status && <span className="text-red-500">{errors.status.message}</span>}
                  </div>
                </div>

                {/* Row 6: Status Reason */}
                <div className="grid grid-cols-1 gap-3 md:gap-8">
                  <div className="flex flex-col w-full space-y-1">
                    <label htmlFor="statusReason" className="text-text-hint mb-1">
                      Status Reason
                    </label>
                    <textarea
                      {...register('statusReason')}
                      name="statusReason"
                      placeholder="Status Reason"
                      id="statusReason"
                      className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Row 6: Contact Name, Contact Email, Contact Phone */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="jobContactName" className="text-text-hint mb-1">
                      Contact Name <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactName', { required: 'Job Contact Name is required' })}
                      placeholder="Job Contact Name"
                      type="text"
                      id="jobContactName"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactName && (
                      <span className="text-red-500">{errors.jobContactName.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="jobContactEmail" className="text-text-hint mb-1">
                      Contact Email <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactEmail', {
                        required: 'Job Contact Email is required',
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: 'Must be a valid email address',
                        },
                      })}
                      type="email"
                      placeholder="Job Contact Email"
                      id="jobContactEmail"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactEmail && (
                      <span className="text-red-500">{errors.jobContactEmail.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="jobContactPhoneNum" className="text-text-hint mb-1">
                      Contact Phone Number <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactPhoneNum', { required: 'Job Contact Phone Number is required' })}
                      placeholder="(999) 999 9999"
                      id="jobContactPhoneNum"
                      type="text"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactPhoneNum && (
                      <span className="text-red-500">{errors.jobContactPhoneNum.message}</span>
                    )}
                  </div>
                </div>

                {/* Row 7: Job Description */}
                <div className="grid grid-cols-1 gap-3 md:gap-8">
                  <div className="flex flex-col w-full space-y-1">
                    <label htmlFor="jobDescription" className="text-text-hint mb-1">
                      Job Description <span className="text-danger-500">*</span>
                    </label>
                    <textarea
                      {...register('jobDescription', { required: 'Job Description is required' })}
                      placeholder="Job Description"
                      id="jobDescription"
                      className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                      rows={4}
                    />
                    {errors.jobDescription && (
                      <span className="text-red-500">{errors.jobDescription.message}</span>
                    )}
                  </div>
                </div>

                {/* Audit Footer - Edit Mode Only */}
                {marketingJob.jobId && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Created By</label>
                      <p className="text-sm text-gray-600">{marketingJob.created_by || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Date Created</label>
                      <p className="text-sm text-gray-600">
                        {marketingJob.created ? formatDateCST(marketingJob.created) : 'N/A'}
                      </p>
                      {marketingJob.created && (
                        <p className="text-xs text-gray-500">{formatTimeCST(marketingJob.created)}</p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Updated By</label>
                      <p className="text-sm text-gray-600">{marketingJob.updated_by || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Last Updated</label>
                      <p className="text-sm text-gray-600">
                        {marketingJob.updated ? formatDateCST(marketingJob.updated) : 'N/A'}
                      </p>
                      {marketingJob.updated && (
                        <p className="text-xs text-gray-500">{formatTimeCST(marketingJob.updated)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                    <button
                      type="submit"
                      disabled={!isValid || (marketingJob.jobId && !isDirty)}
                      className={`py-2 px-6 rounded-lg shadow transition ${isValid && (!marketingJob.jobId || isDirty) ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal>

        {/* Contacts Modal for selecting a contact */}
        <Modal open={isContactsOpen} onClose={() => setIsContactsModalOpen(false)}>
          <div className="p-4">
            <SalesContacts onSelectContact={handleContactSelect} isSelectionMode={true} />
          </div>
        </Modal>

        {/* Edit Contact Modal */}
        <SalesContactsModal
          isOpen={isEditContactModalOpen}
          onClose={() => {
            setIsEditContactModalOpen(false);
            setEditContactData(null);
          }}
          onSubmit={handleEditContactSubmit}
          isEditMode={true}
          defaultValues={editContactData}
        />

        {/* Assign Job Modal */}
        {isAssignJobModalOpen && (
          <AssignJobFromCandidateModal
            isOpen={isAssignJobModalOpen}
            onClose={closeAssignJobModal}
            candidateId={candidateId}
            candidateSalesPersonId={candidateSalesPersonId}
            onSaveSuccess={getData}
          />
        )}
      </div>
    </div>
  );
};

export default MarketingJobs;
